import { Action } from '@ngrx/store';
import { Option } from '@sapphire/result';

import { MC_MESSAGES } from '@ps/survey-utils';

import {
  isSaveable,
  SaveGateOptions,
  saveGateForms,
} from './survey-save-pipeline.helper';
import { SurveyFormContext } from './survey-form-context.helper';
import {
  SurveyUpdateOverrides,
  ValidatedSurveyForSave,
} from '../pipeline/survey-save.pipeline.types';

// Only `saveSurvey` carries the flag; every other save reports.
export function isSilentSave(trigger: Action): boolean {
  return 'silent' in trigger && trigger.silent === true;
}

// Only `saveAndOpenManageSurvey` carries the launched-survey pro-rata choice.
function proRataChoiceOf(trigger: Action): Option<boolean> {
  return 'updateSupplier' in trigger &&
    typeof trigger.updateSupplier === 'boolean'
    ? Option.some(trigger.updateSupplier)
    : Option.none;
}

// Payload input carried on the action; none for every other save.
export function overridesFromTrigger(trigger: Action): SurveyUpdateOverrides {
  return proRataChoiceOf(trigger).mapOr({}, (updateSupplier) => ({
    updateSupplier,
  }));
}

// Gate and narrowing in one step, so they cannot drift apart.
export function toValidatedSurvey(
  context: SurveyFormContext,
  gate: SaveGateOptions,
  triggerOverrides: SurveyUpdateOverrides,
): Option<ValidatedSurveyForSave> {
  const { trigger, surveyId, audienceForm, launchForm, marginForm } = context;

  return Option.from(surveyId)
    .filter(() => isSaveable(audienceForm, launchForm, marginForm, gate))
    .andThen((validatedSurveyId) =>
      Option.from(audienceForm.data).map(
        (data): ValidatedSurveyForSave => ({
          surveyId: validatedSurveyId,
          silent: isSilentSave(trigger),
          triggerOverrides,
          data,
          launch: launchForm.data,
          marginForm,
          pricing: context.pricing,
          advancedFeasibilityFilters: context.advancedFeasibilityFilters,
          respondentListPsids: context.respondentListPsids,
          locationFileUpload: context.locationFileUpload,
        }),
      ),
    );
}

// The first blocking form error, for a survey reported on its own.
export function firstFormError({
  audienceForm,
  launchForm,
  marginForm,
}: SurveyFormContext): string {
  return Option.from(
    saveGateForms(audienceForm, launchForm, marginForm).find(
      (form) => !form.valid,
    ),
  )
    .andThen((form) => Option.from(form.error))
    .unwrapOr(MC_MESSAGES.LOCALE_NOT_SAVEABLE);
}
